/**
 * tonkSounds — synth-based card sound effects + haptic helper.
 *
 * All sounds are synthesized via Web Audio API — no external files, no
 * network, no bundle bloat. Each call is <200 ms and non-blocking.
 *
 * The AudioContext is created lazily on the first play(...) call so we
 * don't trip Chrome/Safari's autoplay policy — the user's first gesture
 * (hitting DEAL, tapping a card, etc.) fires a sound which sets it up.
 *
 * Persistent mute state lives in `localStorage['tonk-sound-muted']`.
 */

let _ctx = null;
let _muted = false;

try {
    _muted = localStorage.getItem('tonk-sound-muted') === '1';
} catch { /* ignore */ }

function ctx() {
    if (_muted) return null;
    if (!_ctx) {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            _ctx = new AC();
        } catch { _ctx = null; }
    }
    if (_ctx && _ctx.state === 'suspended') {
        _ctx.resume().catch(() => { /* ignore */ });
    }
    return _ctx;
}

export function setMuted(m) {
    _muted = !!m;
    try { localStorage.setItem('tonk-sound-muted', _muted ? '1' : '0'); } catch { /* ignore */ }
}
export function isMuted() { return _muted; }

/** Card flick — brief filtered-noise burst. Used for dealing / drawing. */
export function playFlick(volume = 0.28) {
    const c = ctx();
    if (!c) return;
    const now = c.currentTime;
    const dur = 0.12;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const filter = c.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2400;
    filter.Q.value = 4;
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    src.connect(filter).connect(gain).connect(c.destination);
    src.start(now);
    src.stop(now + dur);
}

/** Soft thud — used for discard landing on the pile. */
export function playThud() {
    const c = ctx();
    if (!c) return;
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(65, now + 0.09);
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.32, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    osc.connect(gain).connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.2);
    // A brief noise transient on top to mimic the paper hitting felt
    playFlick(0.15);
}

/** Deal a burst of flicks — one per card, staggered ~90 ms. */
export function playDeal(cardCount = 5, playerCount = 2) {
    const c = ctx();
    if (!c) return;
    const total = cardCount * playerCount;
    for (let i = 0; i < total; i += 1) {
        setTimeout(() => playFlick(0.2), i * 85);
    }
}

/** Spread lay — 3 quick flicks in rapid succession. */
export function playSpread() {
    playFlick(0.3);
    setTimeout(() => playFlick(0.3), 60);
    setTimeout(() => playFlick(0.3), 120);
}

/** Hit — a bright chip-clink chime. */
export function playHit() {
    const c = ctx();
    if (!c) return;
    const now = c.currentTime;
    [1200, 1800].forEach((freq, i) => {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + i * 0.03);
        gain.gain.setValueAtTime(0.001, now + i * 0.03);
        gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.03 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.03 + 0.15);
        osc.connect(gain).connect(c.destination);
        osc.start(now + i * 0.03);
        osc.stop(now + i * 0.03 + 0.16);
    });
}

/** Win chime — ascending C-E-G-C triangle-wave arpeggio. */
export function playWin() {
    const c = ctx();
    if (!c) return;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
        setTimeout(() => {
            const cc = ctx();
            if (!cc) return;
            const now = cc.currentTime;
            const osc = cc.createOscillator();
            const gain = cc.createGain();
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(freq, now);
            gain.gain.setValueAtTime(0.001, now);
            gain.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
            osc.connect(gain).connect(cc.destination);
            osc.start(now);
            osc.stop(now + 0.4);
        }, i * 90);
    });
}

/** Loss buzzer — short descending sawtooth blat. */
export function playLoss() {
    const c = ctx();
    if (!c) return;
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(240, now);
    osc.frequency.exponentialRampToValueAtTime(90, now + 0.28);
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.14, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
    osc.connect(gain).connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.35);
}

/** Brief haptic pulse — no-op when unsupported (desktop / older iOS). */
export function haptic(ms = 15) {
    try {
        if (navigator?.vibrate) navigator.vibrate(ms);
    } catch { /* ignore */ }
}
